/* eslint-disable react/prop-types */
import React from "react";
import PropTypes from "prop-types";
import MMTCLOGO from "../../icons/MMTCLOGO.svg";
import VectorTopRight from "../../icons/VectorTopRight.svg";
import VectorBottomLeft from "../../icons/VectorBottomLeft.svg";
import { Info } from "lucide-react";
import { Button } from "../ui/button";


const VARIANTS = {
  v1: {
    card: "w-full bg-white border border-[#E5E5E5] rounded-2xl shadow-[0px_8px_30px_rgba(0,0,0,0.08)] p-8 flex flex-col gap-6",
    valueText: "",
    headerText: "text-2xl font-semibold",
  },
  v2: {
    card: "w-full bg-white border border-[#f0f0f0] rounded-[10px] shadow-[0px_4px_14px_0px_rgba(0,0,0,0.12)] p-[30px] flex flex-col gap-5",
    valueText: "text-base",
    headerText: "text-[28px] font-semibold",
  },
};

const Divider = () => <div className="h-px bg-[#EEEEEE]" />;

const LoanInfoRow = ({ label, value, values, customClassName }) => (
  <div className='flex flex-col gap-[2px]'>
    <span className='text-sm text-[#3c3c3c]'>{label}</span>
    {values ? (
      <ul className={`text-black list-disc list-inside ${customClassName}`}>
        {values.map((v, i) => <li key={i}>{v}</li>)}
      </ul>
    ) : (
      <span className={`text-black ${customClassName}`}>{value}</span>
    )}
  </div>
);

const LiquidityBox = ({ label, value, isLiquidityOverage }) => {
  let colorClass = "text-black";
  if (isLiquidityOverage === true) colorClass = "text-[#007D06]";
  else if (isLiquidityOverage === false) colorClass = "text-[#C5140D]";

  return (
    <div className='flex-1 bg-[#EEF3FF] border border-[#3861FB3D] rounded-lg p-4 flex flex-col gap-2'>
      <span className='text-xs text-[#666666] leading-tight'>{label}</span>
      <span className={`text-lg font-bold ${colorClass}`}>{value || "$0"}</span>
    </div>
  );
};

const LoanInfoCard = ({ apiData, onAddMoreAccounts, version }) => {
  const {
    loanId,
    borrowerNames,
    propertyAddresses,
    borrowerLiquidity,
    totalLiquidity,
    shortageOrOverage,
    isLiquidityOverage,
    totalLoanAmount,
  } = apiData;

  const isV2 = version === "v2";
  const styles = VARIANTS[isV2 ? "v2" : "v1"];

  const warningBanner = (
    <div className="flex items-center gap-2 bg-[#EBEFFF] border border-[rgba(56,97,251,0.24)] rounded-[12px] p-4">
      <Info className="text-system-info-2" />
      <p className="flex-1 text-[14px] font-medium leading-5 text-[#262626]">
        While selecting new accounts, please don&rsquo;t select already added accounts.
      </p>
    </div>
  );

  const addMoreButton = (
    <Button
      variant="default"
      size="default"
      className={isV2
        ? "bg-secondary-2 text-white hover:shadow-md w-[267px] rounded-[10px]"
        : "bg-secondary-2 text-white hover:shadow-md"
      }
      onClick={onAddMoreAccounts}
    >
      Add More Accounts
    </Button>
  );

  return (
    <div className={styles.card}>
      {/* Row 1: Loan ID, Loan Amount, + Total Liquidity Required (v2 only) */}
      <div className="flex justify-between items-start capitalize">
        <LoanInfoRow label="Loan ID:" value={loanId} customClassName={styles.headerText} />
        <LoanInfoRow label="Loan Amount:" value={totalLoanAmount} customClassName={styles.headerText} />
        {isV2 && (
          <LoanInfoRow label="Total Liquidity Required:" value={totalLiquidity} customClassName={styles.headerText} />
        )}
      </div>
      <Divider />

      {/* Row 2: Borrower & Property Address */}
      <div className="flex justify-between items-start capitalize">
        <LoanInfoRow label="Borrower" values={borrowerNames?.length ? borrowerNames : ["N/A"]} customClassName={styles.valueText} />
        <LoanInfoRow label="Property Address" values={propertyAddresses?.length ? propertyAddresses : ["N/A"]} customClassName={styles.valueText} />
      </div>
      <Divider />

      {/* Row 3: Liquidity boxes (v1 only) */}
      {!isV2 && (
        <>
          <div className="flex gap-4">
            <LiquidityBox label="Total Borrower Liquidity ($)" value={borrowerLiquidity} />
            <LiquidityBox label="Total Liquidity Required ($)" value={totalLiquidity} />
            <LiquidityBox
              label={isLiquidityOverage ? "Liquidity Overage" : "Liquidity Shortage"}
              value={shortageOrOverage}
              isLiquidityOverage={isLiquidityOverage}
            />
          </div>
          <Divider />
        </>
      )}

      {/* Row 4: Warning + Button */}
      {isV2 ? (
        <div className="flex flex-col gap-6">
          {warningBanner}
          <div className="flex items-center justify-center">
            {addMoreButton}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#595959] leading-6 max-w-[291px]">
            While selecting new accounts, please don&rsquo;t select already added accounts.
          </p>
          {addMoreButton}
        </div>
      )}
    </div>
  );
};

export const AccountAddedSuccessfully = ({
  apiData,
  onAddMoreAccounts = () => window.location.reload(),
  version = "v2",
}) => {
  return (
    <div className='min-h-screen bg-white flex flex-col items-center justify-center relative overflow-hidden font-sans'>
      <VectorTopRight className='absolute top-0 right-0 pointer-events-none' />
      <VectorBottomLeft className='absolute bottom-0 left-0 pointer-events-none' />

      <div className='flex flex-col items-center gap-8 z-10 w-full max-w-[691px] px-4 py-10'>
        <MMTCLOGO className='w-[148px] h-[49px]' />

        <div className='flex flex-col items-center gap-6 w-full'>
          {/* Success header */}
          <div className='flex flex-col items-center gap-2'>
            <h2 className='text-[28px] font-bold text-black text-center'>
              Account Added Successfully!
            </h2>
            <p className='text-base text-[#3c3c3c] text-center'>
              Your account has been added successfully. You&rsquo;re all set to move forward.
            </p>
          </div>

          {/* Loan info card */}
          <LoanInfoCard apiData={apiData} onAddMoreAccounts={onAddMoreAccounts} version={version} />
        </div>
      </div>
    </div>
  );
};

AccountAddedSuccessfully.propTypes = {
  apiData: PropTypes.object.isRequired,
  onAddMoreAccounts: PropTypes.func,
  version: PropTypes.oneOf(["v1", "v2"]),
};
